import type { ApolloClient, ApolloQueryResult } from 'apollo-client';
import type { DocumentNode } from 'graphql';

import { SSRMeasures } from '@confluence/action-measures/entry-points/SSRMeasures.ts';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import { isUnauthorizedError } from '@confluence/unauthorized-error-boundary';
import { markErrorAsHandled } from '@confluence/graphql-error-processor/entry-points/GraphqlErrorProcessor.ts';

import type { SessionDataType } from './SessionDataTypes.ts';
import { SiteInformationQuery } from './SiteInformationQuery.graphql.ts';
import type { SiteInformationQuery as SiteInformationQueryType } from './__types__/SiteInformationQuery.ts';

declare global {
	interface Window {
		__SITEINFORMATION_PROMISE__: Promise<SessionDataType> | null;
		__SITEINFORMATION__:
			| Promise<SiteInformationQueryType | null>
			| SiteInformationQueryType
			| Promise<null>
			| null;
	}
}

function apolloQuery<Q>({
	query,
	queryName,
	client = window.GLOBAL_APOLLO_CLIENT,
}: {
	query: DocumentNode;
	queryName: string;
	client?: ApolloClient<any>;
}): Promise<ApolloQueryResult<Q>> {
	if (!client) {
		return Promise.reject(new Error(`ApolloClient not available, cannot load query ${queryName}`));
	}

	return SSRMeasures.run(`ssr-app/prepare/preloadQuery/fetch:${queryName}`, async () =>
		client.query({
			query,
			errorPolicy: 'all',
			fetchPolicy: 'cache-first',
			context: {
				single: false,
				allowOnExternalPage: true,
			},
		}),
	);
}

export async function loadSessionData(): Promise<SiteInformationQueryType | null> {
	// __SITEINFORMATION__ is either actual session data
	// or the promise that is resolving the session data
	// In the case when page is rendered by SSR, the __SITEINFORMATION__ will be populated with JSON data.
	if (window['__SITEINFORMATION__']) {
		return window['__SITEINFORMATION__'];
	}

	window['__SITEINFORMATION__'] = apolloQuery<SiteInformationQueryType | null>({
		query: SiteInformationQuery,
		queryName: 'SiteInformationQuery',
	})
		.then((result) => {
			const errors = result.errors ?? [];
			if (errors.length > 0) {
				errors.forEach((e) => {
					if (isUnauthorizedError(e) && !!result?.data?.tenant?.cloudId) {
						markErrorAsHandled(e);
					} else {
						getMonitoringClient().submitError(e, {
							isSiteInformationError: true,
							partialFailure: true,
							attribution: 'FATAL',
						});
					}
				});
			}
			return result.data;
		})
		.catch((error) => {
			if (Array.isArray(error)) {
				error.forEach((e) =>
					getMonitoringClient().submitError(e, {
						isSiteInformationError: true,
						partialFailure: false,
						attribution: 'FATAL',
					}),
				);
			} else {
				getMonitoringClient().submitError(error, {
					isSiteInformationError: true,
					partialFailure: false,
					attribution: 'FATAL',
				});
			}

			return null;
		});

	return window['__SITEINFORMATION__'];
}
